import { AlertFactory, AlertState } from 'alerts/Alert'
import { UpdateProviderUserDataPayload } from 'iam/dtos/provider'
import Provider from 'iam/models/Provider'
import ProviderStore from 'iam/ProviderStore'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { Id } from 'types/definitions'
import { areDeepEqual } from 'utils/object-util'
import { func, validationContext } from 'validx'

export default class ProDetailsViewModel {
  @observable softwareOwned: string[] = []
  @observable softwareExperience: string[] = []
  @observable supplementalSoftware: string[] = []
  @observable specialties: string[] = []
  @observable stateRegion: string | undefined = undefined
  @observable yearsExperience = 0

  private provider: Provider = Provider.none

  validation = validationContext(this, {
    yearsExperience: [
      func(
        ({ value }) =>
          (typeof value === 'number' && value >= 0 && value <= 99) ||
          'Years of experience must be between 0 and 99',
      ),
    ],
  })

  constructor(
    private newAlert: AlertFactory,
    private providerStore: ProviderStore,
    private userPublicId: Id,
    public editable: boolean,
  ) {
    makeObservable(this)
  }

  activate() {
    this.load()
  }

  deactivate() {
    //
  }

  load = task(async () => {
    this.provider =
      this.providerStore.primeProvider({
        user_public_id: this.userPublicId,
      }) ?? Provider.none
    await this.providerStore.fetchByPublicId(this.userPublicId)
    this.initializeFromProvider()
  })

  @computed
  get hasChanged() {
    return !areDeepEqual(this.collectFromProvider(), this.collectForUpdate())
  }

  @computed
  get canSave() {
    return this.editable && !this.save.pending && this.hasChanged
  }

  @action.bound
  setSoftwareOwned(value: string[]) {
    this.softwareOwned = value ?? []
  }

  @action.bound
  setSoftwareExperience(value: string[]) {
    this.softwareExperience = value ?? []
  }

  @action.bound
  setSupplementalSoftware(value: string[]) {
    this.supplementalSoftware = value ?? []
  }

  @action.bound
  setSpecialties(value: string[]) {
    this.specialties = value ?? []
  }

  @action.bound
  setStateRegion(value: string | undefined) {
    this.stateRegion = value
  }

  @action.bound
  setYearsExperience(value: number | null) {
    this.yearsExperience = value ?? 0
  }

  save = task.resolved(async (): Promise<boolean> => {
    if (!this.hasChanged) {
      return true
    }

    if (!this.validation.reset().validate().isValid) {
      return false
    }

    try {
      await this.providerStore.update(
        this.userPublicId,
        this.collectForUpdate(),
      )
      this.initializeFromProvider()
      this.newAlert()
        .withState(AlertState.Success)
        .saying('Success!', 'Pro details have been updated.')
        .autoDismiss()
        .show()
      return true
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
      return false
    }
  })

  private collectForUpdate(): UpdateProviderUserDataPayload {
    return {
      software_owned: this.softwareOwned,
      software_experience: this.softwareExperience,
      supplemental_software_experience: this.supplementalSoftware,
      pro_specialties: this.specialties,
      state_region: this.stateRegion ?? '',
      years_experience: this.yearsExperience,
    }
  }

  private collectFromProvider(): UpdateProviderUserDataPayload {
    return {
      software_owned: this.provider.softwareOwned,
      software_experience: this.provider.softwareExperience,
      supplemental_software_experience:
        this.provider.supplementalSoftwareExperience,
      pro_specialties: this.provider.specialties,
      state_region: this.provider.stateRegion,
      years_experience: this.provider.yearsExperience,
    }
  }

  @action.bound
  private initializeFromProvider() {
    this.softwareOwned = [...this.provider.softwareOwned]
    this.softwareExperience = [...this.provider.softwareExperience]
    this.supplementalSoftware = [
      ...this.provider.supplementalSoftwareExperience,
    ]
    this.specialties = [...this.provider.specialties]
    this.stateRegion = this.provider.stateRegion || undefined
    this.yearsExperience = this.provider.yearsExperience
  }
}
